import { memo, useMemo } from 'react';
import type { SortScoreVersion } from '@/atoms/typesenseDebug';
import { FreeFirstTrackSections, type TrackSection } from '@/components/FreeFirstTrackSections';
import { createStaticPageQueueAtom } from '@/features/AudioPlayer/atoms/queue/derivedQueueAtoms';
import { type FreeFirstTierResult, useFreeFirstMusicSearch } from '@/hooks/search/useFreeFirstMusicSearch';
import type { MusicDocument, SearchResponseHit, SortOption } from '@music-vine/setlist';
import { ASSET_TYPE } from '@/types/asset';
import { normalizeTracksDocumentForSearch } from '@/utils/track-documents';
export const convertMusicHits = (hits: SearchResponseHit<MusicDocument>[]) => hits.map(hit => normalizeTracksDocumentForSearch({
  ...hit.document,
  asset_type: 'music',
  featured_tags: hit.document.featured_tags?.map(tag => ({
    ...tag,
    id: tag.slug || '',
    slug: tag.slug || '',
    is_qualified: 1,
    seo_relevance: 0.5
  })) || []
}));
const useTierSection = ({
  tier,
  queueSource,
  queueName,
  queueId,
  isLoading,
  isError
}: {
  tier: FreeFirstTierResult;
  queueSource: string;
  queueName: string;
  queueId: string;
  isLoading: boolean;
  isError: boolean;
}): TrackSection => {
  const results = useMemo(() => convertMusicHits(tier.hits), [tier.hits]);
  const pageQueueAtom = useMemo(() => createStaticPageQueueAtom(tier.hits, {
    source: queueSource,
    sourceType: 'search',
    sourceName: queueName,
    sourceId: queueId
  }), [tier.hits, queueSource, queueName, queueId]);
  return {
    results,
    pageQueueAtom,
    hasMore: tier.hasMore,
    isFetchingMore: tier.isFetchingMore,
    onShowMore: tier.onShowMore,
    pageIndex: tier.pageCount,
    isLoading,
    isError
  };
};
interface FreeFirstSearchResultsProps {
  contextFilter?: string;
  contextPath: string;
  // `false` on global /music/all; the page's public path on collection/tag
  // routes, so split sections hit the same engagement-sorted collection.
  contextSlug: string | false;
  filters: string;
  premiumTitle?: string;
  query: string;
  sort: SortOption;
  sortScoreVersion: SortScoreVersion;
}
export const FreeFirstSearchResults = memo(({
  query,
  filters,
  sort,
  sortScoreVersion,
  contextPath,
  contextSlug,
  contextFilter,
  premiumTitle
}: FreeFirstSearchResultsProps) => {
  const {
    free,
    premium,
    isLoading,
    isError
  } = useFreeFirstMusicSearch({
    query,
    filters,
    sort,
    sortScoreVersion,
    contextPath,
    contextSlug,
    contextFilter
  });
  const freeSection = useTierSection({
    tier: free,
    queueSource: `${contextPath}:free-first-free`,
    queueName: 'Free music',
    queueId: `${query}:free`,
    isLoading,
    isError
  });
  const premiumSection = useTierSection({
    tier: premium,
    queueSource: `${contextPath}:free-first-premium`,
    queueName: 'Premium music',
    queueId: `${query}:premium`,
    isLoading,
    isError
  });
  return <FreeFirstTrackSections assetType={ASSET_TYPE.MUSIC} free={freeSection} premium={premiumSection} premiumTitle={premiumTitle} />;
});
FreeFirstSearchResults.displayName = 'FreeFirstSearchResults';